// ******************************************************************************************
// Copyright (c) 2019-2026 Markadoc Corp and Ontario Partners LLC of NY. All Rights Reserved
// ******************************************************************************************

import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { UserInfo } from "../user-acct/model/userinfo.model";
import { CountData } from "../model/countdata.model";
import { SumData } from "../model/sumdata.model";
import { AuthService } from "./auth.service";

@Injectable({ providedIn: "any" })
export class UserService {
  baseUrl = environment.apiUrl;
  isValid: any;

  public User: UserInfo = null;
  public UserList: UserInfo[] = null;
  UserSystemCnt: CountData = null;
  UserStorageSum: SumData = null;

  private userListItems$: BehaviorSubject<UserInfo[]> = new BehaviorSubject<UserInfo[]>(this.UserList);
  public userList$: Observable<UserInfo[]> = this.userListItems$.asObservable();

  private userItem$: BehaviorSubject<UserInfo> = new BehaviorSubject<UserInfo>(this.User);
  public userInfo$: Observable<UserInfo> = this.userItem$.asObservable();

  private userSystemCountSubj$: BehaviorSubject<CountData> = new BehaviorSubject<CountData>(this.UserSystemCnt);
  userSystemCount$: Observable<CountData> = this.userSystemCountSubj$.asObservable();

  constructor(
    private auth: AuthService,
    private http: HttpClient
  ) {}

  /**
   * Fetch all user accounts and update internal state
   */
  getAllUserAccts$(): void {
    this.http.get<UserInfo[]>(`${this.baseUrl}/user`).subscribe({
      next: (res) => {
        this.UserList = res;
        this.userListItems$.next(res);
        console.log("user acct list =", res);
      },
      error: (err) => {
        console.error("usersvc failed to get user accts:", err);
      }
    });
  }

  // GET ALL USERS AS Observable
  getAllUsers$(uuid?: string): Observable<UserInfo[]> {
    return this.http.get<UserInfo[]>(`${this.baseUrl}/user`);
  }

  getAllUsersPromise(): Promise<UserInfo[]> {
    return this.http
      .get<UserInfo[]>(`${this.baseUrl}/user`)
      .toPromise()
      .then(
        (x) => {
          this.UserList = x;
          this.userListItems$.next(x);
          return x;
        },
        (e) => {
          console.error("usersvc failed to get all user(s), error: ", e);
          return [];
        }
      );
  }

  async getAllUsers(): Promise<UserInfo[]> {
    try {
      return await this.getAllUsersPromise();
    } catch (e) {
      console.error("usersvc getAllUsers error=", e);
      return [];
    }
  }

  // GET USER INFO BY EMAIL
  getUserByEmail$(email: string): Observable<UserInfo> {
    return this.http.get<UserInfo>(`${this.baseUrl}/user/email/${email}`);
  }

  getUserByEmailPromise(email: string): Promise<UserInfo> {
    return this.http
      .get<UserInfo>(`${this.baseUrl}/user/email/${email}`)
      .toPromise()
      .then(
        (x) => x,
        (e) => {
          console.error("usersvc failed to get user by email, error: ", e);
          return null;
        }
      );
  }

  // GET USER INFO BY UID
  getUserInfo$(uuid: string): Observable<UserInfo> {
    return this.http.get<UserInfo>(`${this.baseUrl}/user/${uuid}`).pipe(
      catchError((err) => {
        console.error("usersvc failed to get user via observable: ", err);
        return of(null);
      })
    );
  }

  getUserInfoSubscribe(uuid: string) {
    return this.http.get<UserInfo>(`${this.baseUrl}/user/${uuid}`).subscribe({
      next: (x) => x as UserInfo,
      error: (e) => console.error("usersvc failed to get user(s), error: ", e)
    });
  }

  GetUserInfoObservable(uuid: string): Observable<UserInfo> {
    return this.http.get<UserInfo>(`${this.baseUrl}/user/${uuid}`).pipe(
      map((x) => x as UserInfo),
      catchError((err) => {
        console.error("usersvc GetUserInfoObservable error:", err);
        return of(null);
      })
    );
  }

  getUserInfoPromise(uuid: string): Promise<any> {
    return this.http
      .get<UserInfo>(`${this.baseUrl}/user/${uuid}`)
      .toPromise()
      .then(
        (x) => {
          this.User = x;
          this.userItem$.next(x);
          return x;
        },
        (e) => {
          console.error("usersvc failed to get user(s), error: ", e);
          return null;
        }
      );
  }

  async getUserInfo(uuid: string): Promise<UserInfo> {
    try {
      return await this.getUserInfoPromise(uuid);
    } catch (e) {
      console.error("usersvc getUserInfo error=", e);
      return null;
    }
  }

  // CREATE USER
  addListItem(new_data: UserInfo) {
    if (!this.UserList) {
      this.UserList = [];
    }
    this.UserList.push(new_data);
    this.UserList = Object.assign([], this.UserList);
  }

  addUserInfoPromise(info: any): Promise<UserInfo> {
    return this.http
      .post<UserInfo>(`${this.baseUrl}/user`, info)
      .toPromise()
      .then(
        (x) => {
          this.addListItem(x);
          this.userListItems$.next(this.UserList);
          this.userItem$.next(x);
          return x;
        },
        (e) => {
          console.error("usersvc failed to create user: ", info?.email, " error:", e);
          return null;
        }
      );
  }

  async addUserInfo(info: any): Promise<UserInfo> {
    try {
      return await this.addUserInfoPromise(info);
    } catch (e) {
      console.error("usersvc: addUserInfo error=", e);
      return null;
    }
  }

  // UPDATE USER
  updateListItem(newItem: UserInfo) {
    if (this.UserList) {
      const idx = this.UserList.findIndex((item) => item.uid === newItem.uid);
      if (idx >= 0) {
        this.UserList[idx] = newItem;
        this.UserList = Object.assign([], this.UserList);
      }
    }
  }

  updateUserInfoPromise(uuid: string, info: any): Promise<UserInfo> {
    return this.http
      .put<UserInfo>(`${this.baseUrl}/user/${uuid}`, info)
      .toPromise()
      .then(
        (x) => {
          this.updateListItem(x);
          this.userListItems$.next(this.UserList);
          this.userItem$.next(x);
          return x;
        },
        (e) => {
          console.error("usersvc failed to edit user: ", info?.email, " error:", e);
          return null;
        }
      );
  }

  async updateUserInfo(uuid: string, info: any): Promise<UserInfo> {
    try {
      return await this.updateUserInfoPromise(uuid, info);
    } catch (e) {
      console.error("usersvc: updateUserInfo error=", e);
      return null;
    }
  }

  // DELETE USER
  removeListItem(del_uid: string) {
    if (this.UserList) {
      const idx = this.UserList.findIndex((obj) => obj.uid === del_uid);
      if (idx >= 0) {
        this.UserList.splice(idx, 1);
        this.UserList = Object.assign([], this.UserList);
      }
    }
  }

  deleteUserInfoPromise(uuid: string): Promise<any> {
    return this.http
      .delete(`${this.baseUrl}/user/${uuid}`)
      .toPromise()
      .then(
        (x) => {
          this.removeListItem(uuid);
          this.userListItems$.next(this.UserList);
          return x;
        },
        (e) => {
          console.error("usersvc failed to delete user, error:", e);
          return null;
        }
      );
  }

  async deleteUserInfo(uuid: string): Promise<any> {
    try {
      return await this.deleteUserInfoPromise(uuid);
    } catch (e) {
      console.error("usersvc: deleteUserInfo error=", e);
      return null;
    }
  }

  // USER PASSWORD & NOTIFICATIONS
  updateUserPasswordPromise(uuid: string, pwd: string): Promise<any> {
    const pwdinfo = { password: pwd };
    return this.http
      .put(`${this.baseUrl}/user/${uuid}`, pwdinfo)
      .toPromise()
      .then(
        (x) => x,
        (e) => {
          console.error("usersvc failed to update password: ", e);
          return null;
        }
      );
  }

  async updateUserPassword(uuid: string, pwd: string): Promise<any> {
    try {
      return await this.updateUserPasswordPromise(uuid, pwd);
    } catch (e) {
      console.error("usersvc: updateUserPassword error=", e);
      return null;
    }
  }

  updateUserNotificationsPromise(uuid: string, setting: boolean): Promise<any> {
    const notify = { notify: setting };
    return this.http
      .put(`${this.baseUrl}/user/${uuid}`, notify)
      .toPromise()
      .then(
        (x) => x,
        (e) => {
          console.error("usersvc failed to update notifications: ", e);
          return null;
        }
      );
  }

  async updateUserNotifications(uuid: string, notify: boolean): Promise<any> {
    try {
      const res = await this.updateUserNotificationsPromise(uuid, notify);
      this.auth.clearReset();
      return res;
    } catch (e) {
      console.error("usersvc: updateUserNotifications error=", e);
      return null;
    }
  }

  private handleError<T>(operation = "operation", result?: T) {
    return (error: any): T => {
      console.error(`${operation} failed: ${error.message} error: ${error}`);
      return result as T;
    };
  }

  // STATS / COUNTS
  getUserSystemCount$(): void {
    this.http.get<CountData>(`${this.baseUrl}/user/count/system`).subscribe({
      next: (cnt) => {
        console.log("user cnt =", cnt);
        this.userSystemCountSubj$.next(cnt);
      },
      error: (err) => {
        console.error("usersvc failed to fetch user system count:", err);
      }
    });
  }
}